import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { hubService, Hub, CreateHubData, UpdateHubData } from '../api/hubService';
import { userService } from '../api/userService';
import { MapContainer, TileLayer, useMap, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

// Generate consistent colors for hubs
const generateHubColor = (hubId: number): string => {
  const colors = [
    '#ef4444', // red
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#f97316', // orange
    '#6366f1', // indigo
  ];
  return colors[hubId % colors.length];
};

// Component to display hub labels on map
const HubLabels: React.FC<{ hubs: Hub[]; excludeId?: number }> = ({ hubs, excludeId }) => {
  const map = useMap();

  useEffect(() => {
    const labels: L.Marker[] = [];

    hubs.forEach(hub => {
      if (hub.regionGeoJson && hub.id !== excludeId) {
        const color = generateHubColor(hub.id);
        const layer = L.geoJSON(hub.regionGeoJson);
        const bounds = layer.getBounds();
        const center = bounds.getCenter();
        
        const label = L.marker(center, {
          icon: L.divIcon({
            className: 'hub-label',
            html: `<div style="background: white; padding: 2px 6px; border-radius: 4px; border: 2px solid ${color}; font-size: 12px; font-weight: bold; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">${hub.name}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          })
        }).addTo(map);
        
        labels.push(label);
      }
    });

    return () => {
      labels.forEach(label => map.removeLayer(label));
    };
  }, [map, hubs, excludeId]);

  return null;
};

// Component to display other hub regions for context
const OtherHubRegions: React.FC<{ hubs: Hub[]; currentHubId?: number }> = ({ hubs, currentHubId }) => {
  const map = useMap();

  useEffect(() => {
    const layers: L.Layer[] = [];

    // Display all other hub regions
    hubs.forEach(hub => {
      if (hub.regionGeoJson && hub.id !== currentHubId) {
        const color = generateHubColor(hub.id);
        const layer = L.geoJSON(hub.regionGeoJson, {
          style: {
            fillColor: color,
            fillOpacity: 0.15,
            color: color,
            weight: 2,
            dashArray: '5, 5',
          },
          // @ts-ignore - pm options for snapping
          snapIgnore: false,
        }).addTo(map);

        // Add popup with hub name
        layer.bindPopup(`<strong>${hub.name}</strong><br/><small>Existing region</small>`);
        
        // Enable snapping for this layer
        // @ts-ignore
        layer.eachLayer((subLayer: any) => {
          if (subLayer.pm) {
            subLayer.pm.enable({
              preventMarkerRemoval: true,
              draggable: false,
            });
            subLayer.pm.disable(); // Disable editing but keep it as a snap target
          }
        });
        
        layers.push(layer);
      }
    });

    // Cleanup on unmount
    return () => {
      layers.forEach(layer => map.removeLayer(layer));
    };
  }, [map, hubs, currentHubId]);

  return null;
};

// Component to initialize Geoman controls for creating regions
const GeomanDrawControl: React.FC<{ inputId: string; existingGeoJson?: any }> = ({ inputId, existingGeoJson }) => {
  const map = useMap();

  useEffect(() => {
    // @ts-ignore - pm plugin augments leaflet
    if (!map.pm) {
      console.error('Leaflet.Geoman not loaded');
      return;
    }

    // Enable global snapping
    // @ts-ignore
    map.pm.setGlobalOptions({
      snappable: true,
      snapDistance: 20,
      snapMiddle: true,
      snapSegment: true,
    });

    // Add drawing controls
    // @ts-ignore
    map.pm.addControls({
      position: 'topright',
      drawCircle: false,
      drawCircleMarker: false,
      drawMarker: false,
      drawPolyline: false,
    });

    // Set drawing style to green
    // @ts-ignore
    map.pm.setPathOptions({
      fillColor: '#22c55e',
      fillOpacity: 0.2,
      color: '#16a34a',
      weight: 3,
    });

    // Load existing GeoJSON if provided
    if (existingGeoJson) {
      const layer = L.geoJSON(existingGeoJson, {
        style: {
          fillColor: '#22c55e',
          fillOpacity: 0.2,
          color: '#16a34a',
          weight: 3,
        }
      }).addTo(map);
      map.fitBounds(layer.getBounds());
    }

    // Handle creation of new shapes
    const handleCreate = (e: any) => {
      const geojson = e.layer.toGeoJSON().geometry;
      const input = document.querySelector(`#${inputId}`) as HTMLInputElement | null;
      if (input) {
        input.value = JSON.stringify(geojson);
        console.log('GeoJSON saved:', geojson);
      }
    };

    // Handle editing of shapes
    const handleEdit = () => {
      // @ts-ignore
      const layers = map.pm.getGeomanLayers?.();
      if (layers && layers.length > 0) {
        const first = layers[0];
        const input = document.querySelector(`#${inputId}`) as HTMLInputElement | null;
        if (first && input) {
          // @ts-ignore - toGeoJSON is added by Leaflet.Geoman
          const geojson = first.toGeoJSON().geometry;
          input.value = JSON.stringify(geojson);
          console.log('GeoJSON updated:', geojson);
        }
      }
    };

    map.on('pm:create', handleCreate);
    map.on('pm:edit', handleEdit);
    map.on('pm:remove', () => {
      const input = document.querySelector(`#${inputId}`) as HTMLInputElement | null;
      if (input) input.value = '';
    });

    // Cleanup
    return () => {
      map.off('pm:create', handleCreate);
      map.off('pm:edit', handleEdit);
      // @ts-ignore
      map.pm.removeControls();
    };
  }, [map, inputId, existingGeoJson]);

  return null;
};

const Hubs: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);

  // Fetch hubs
  const { data: hubsData, isLoading } = useQuery({
    queryKey: ['hubs', page, search],
    queryFn: () => hubService.getAllHubs({ page, limit: 20, search: search || undefined }),
  });

  // Fetch hub leaders
  const { data: hubLeadersData } = useQuery({
    queryKey: ['hubLeaders'],
    queryFn: () => userService.getUsersByRole('hub_leader'),
  });

  const hubs = hubsData?.data || [];
  const pagination = hubsData?.pagination;
  const hubLeaders = hubLeadersData?.data || [];

  // Create hub mutation
  const createHubMutation = useMutation({
    mutationFn: (data: CreateHubData) => hubService.createHub(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      setShowCreateModal(false);
    },
  });

  // Update hub mutation
  const updateHubMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHubData }) =>
      hubService.updateHub(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      setShowEditModal(false);
      setSelectedHub(null);
    },
  });

  // Delete hub mutation
  const deleteHubMutation = useMutation({
    mutationFn: (id: number) => hubService.deleteHub(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubs'] });
      setShowDeleteModal(false);
      setSelectedHub(null);
    },
  });

  const handleCreateHub = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const hubLeaderId = formData.get('hubLeaderId') as string;
    
    const data: CreateHubData = {
      name: formData.get('name') as string,
      hubLeaderId: hubLeaderId ? parseInt(hubLeaderId) : undefined,
      description: formData.get('description') as string || undefined,
      isActive: formData.get('isActive') === 'true',
      centerLatitude: formData.get('centerLatitude') ? parseFloat(formData.get('centerLatitude') as string) : null,
      centerLongitude: formData.get('centerLongitude') ? parseFloat(formData.get('centerLongitude') as string) : null,
      regionGeoJson: formData.get('regionGeoJson') ? JSON.parse(formData.get('regionGeoJson') as string) : null,
    };
    createHubMutation.mutate(data);
  };

  const handleUpdateHub = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedHub) return;
    
    const formData = new FormData(e.currentTarget);
    const hubLeaderId = formData.get('hubLeaderId') as string;
    
    const data: UpdateHubData = {
      name: formData.get('name') as string,
      hubLeaderId: hubLeaderId ? parseInt(hubLeaderId) : null,
      description: formData.get('description') as string || undefined,
      isActive: formData.get('isActive') === 'true',
      centerLatitude: formData.get('centerLatitude') ? parseFloat(formData.get('centerLatitude') as string) : null,
      centerLongitude: formData.get('centerLongitude') ? parseFloat(formData.get('centerLongitude') as string) : null,
      regionGeoJson: formData.get('regionGeoJson') ? JSON.parse(formData.get('regionGeoJson') as string) : null,
    };
    updateHubMutation.mutate({ id: selectedHub.id, data });
  };


  const [showMapView, setShowMapView] = useState(false);

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Hub Management</h1>
              <p className="mt-2 text-sm text-gray-600">
                Create and manage hubs, assign hub leaders
              </p>
            </div>
            <button
              onClick={() => setShowMapView(!showMapView)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {showMapView ? (
                <>
                  <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  List View
                </>
              ) : (
                <>
                  <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Map View
                </>
              )}
            </button>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search hubs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Hub
              </button>
            </div>
          </div>
        </div>

        {/* Map View or Table */}
        {showMapView ? (
          <div className="bg-white shadow rounded-lg overflow-hidden" style={{ height: '600px' }}>
            <MapContainer
              style={{ height: '100%', width: '100%' }}
              center={[32.7, -89.7]}
              zoom={7}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              {/* Display all hub regions */}
              {hubs.map((hub: Hub) => {
                if (!hub.regionGeoJson) return null;
                const color = generateHubColor(hub.id);
                return (
                  <GeoJSON
                    key={hub.id}
                    data={hub.regionGeoJson}
                    style={{
                      fillColor: hub.isActive ? color : '#9ca3af',
                      fillOpacity: hub.isActive ? 0.25 : 0.15,
                      color: hub.isActive ? color : '#6b7280',
                      weight: 2,
                    }}
                    onEachFeature={(feature, layer) => {
                      layer.bindPopup(`
                        <div>
                          <h3 class="font-bold text-lg">${hub.name}</h3>
                          ${hub.description ? `<p class="text-sm text-gray-600">${hub.description}</p>` : ''}
                          <p class="text-sm mt-1">
                            <strong>Leader:</strong> ${hub.hubLeader ? `${hub.hubLeader.firstName} ${hub.hubLeader.lastName}` : 'Unassigned'}
                          </p>
                          <p class="text-sm">
                            <strong>Planters:</strong> ${hub.planterCount || 0}
                          </p>
                          <p class="text-sm">
                            <strong>Status:</strong> <span class="${hub.isActive ? 'text-green-600' : 'text-gray-600'}">${hub.isActive ? 'Active' : 'Inactive'}</span>
                          </p>
                        </div>
                      `);
                    }}
                  />
                );
              })}
              {/* Display labels for all hubs */}
              <HubLabels hubs={hubs} />
            </MapContainer>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
          <ResponsiveTableWrapper>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Hub Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Hub Leader
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Church Planters
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                    Loading...
                  </td>
                </tr>
              ) : hubs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500 sm:px-6">
                    No hubs found
                  </td>
                </tr>
              ) : (
                hubs.map((hub: Hub) => (
                  <tr key={hub.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{hub.name}</div>
                      {hub.description && (
                        <div className="text-sm text-gray-500">{hub.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-normal text-sm text-gray-900 sm:px-6 sm:whitespace-nowrap">
                      {hub.hubLeader ? (
                        `${hub.hubLeader.firstName} ${hub.hubLeader.lastName}`
                      ) : (
                        <span className="text-gray-500">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sm:px-6">
                      {hub.planterCount || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          hub.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {hub.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                      <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:items-center">
                        <button
                          onClick={() => {
                            setSelectedHub(hub);
                            setShowEditModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedHub(hub);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </ResponsiveTableWrapper>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                  disabled={page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{page}</span> of{' '}
                    <span className="font-medium">{pagination.pages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                      disabled={page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
          </div>
        )}

        {/* Create Hub Modal */}
        {showCreateModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCreateModal(false)}></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleCreateHub}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create New Hub</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hub Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hub Leader</label>
                        <select
                          name="hubLeaderId"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        >
                          <option value="">Select Hub Leader</option>
                          {hubLeaders.map((leader) => (
                            <option key={leader.id} value={leader.id}>
                              {leader.firstName} {leader.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          name="description"
                          rows={3}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          value="true"
                          defaultChecked
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm text-gray-700">Active</label>
                      </div>
                      {/* Map region inputs */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Center Latitude</label>
                          <input name="centerLatitude" type="number" step="0.000001" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Center Longitude</label>
                          <input name="centerLongitude" type="number" step="0.000001" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Region Boundary (draw on map)</label>
                        <input id="create-region-geojson" name="regionGeoJson" type="hidden" />
                        <div className="mt-2 h-64 rounded-md overflow-hidden border">
                          <MapContainer
                            style={{ height: '100%', width: '100%' }}
                            center={[32.7, -89.7]}
                            zoom={7}
                          >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
                            <OtherHubRegions hubs={hubs} />
                            <GeomanDrawControl inputId="create-region-geojson" />
                          </MapContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Hub Full Screen View */}
        {showEditModal && selectedHub && (
          <div className="fixed z-50 inset-0 bg-white overflow-hidden">
            <div className="h-screen flex flex-col">
              {/* Header */}
              <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Edit Hub: {selectedHub.name}</h2>
                    <p className="mt-1 text-sm text-gray-500">Define the hub's geographic region and details</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedHub(null);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex overflow-hidden">
                {/* Form Sidebar */}
                <div className="w-96 bg-gray-50 border-r border-gray-200 p-6 overflow-y-auto flex-shrink-0">
                  <form id="edit-hub-form" onSubmit={handleUpdateHub}>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hub Name *</label>
                        <input
                          type="text"
                          name="name"
                          defaultValue={selectedHub.name}
                          required
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hub Leader</label>
                        <select
                          name="hubLeaderId"
                          defaultValue={selectedHub.hubLeaderId || ''}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        >
                          <option value="">Unassigned</option>
                          {hubLeaders.map((leader) => (
                            <option key={leader.id} value={leader.id}>
                              {leader.firstName} {leader.lastName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          name="description"
                          rows={4}
                          defaultValue={selectedHub.description || ''}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          value="true"
                          defaultChecked={selectedHub.isActive}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 text-sm font-medium text-gray-700">Active</label>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-sm font-medium text-gray-900 mb-4">Map Center (Optional)</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Center Latitude</label>
                            <input
                              name="centerLatitude"
                              type="number"
                              step="0.000001"
                              defaultValue={selectedHub.centerLatitude ?? ''}
                              placeholder="32.7 (Mississippi)"
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Center Longitude</label>
                            <input
                              name="centerLongitude"
                              type="number"
                              step="0.000001"
                              defaultValue={selectedHub.centerLongitude ?? ''}
                              placeholder="-89.7 (Mississippi)"
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      </div>

                      <input id="edit-region-geojson" name="regionGeoJson" type="hidden" defaultValue={selectedHub.regionGeoJson ? JSON.stringify(selectedHub.regionGeoJson) : ''} />

                      <div className="border-t border-gray-200 pt-6 mt-6">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Map Legend</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center">
                            <div className="w-4 h-4 border-2 border-green-600 bg-green-100 rounded mr-2"></div>
                            <span className="text-gray-700">Current hub region (editable)</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-4 h-4 border-2 border-blue-500 bg-blue-50 rounded mr-2" style={{ borderStyle: 'dashed' }}></div>
                            <span className="text-gray-700">Other hub regions</span>
                          </div>
                        </div>
                        <p className="mt-3 text-xs text-gray-500">
                          💡 <strong>Tip:</strong> Your drawing will snap to nearby boundaries of other hubs (within 20px). Click on blue regions to see hub names.
                        </p>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Map Area */}
                <div className="flex-1 relative">
                  <div className="absolute inset-0">
                    <MapContainer
                      style={{ height: '100%', width: '100%' }}
                      center={[(selectedHub.centerLatitude ?? 32.7) as number, (selectedHub.centerLongitude ?? -89.7) as number]}
                      zoom={selectedHub.centerLatitude && selectedHub.centerLongitude ? 8 : 7}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
                      <OtherHubRegions hubs={hubs} currentHubId={selectedHub.id} />
                      <HubLabels hubs={hubs} excludeId={selectedHub.id} />
                      <GeomanDrawControl inputId="edit-region-geojson" existingGeoJson={selectedHub.regionGeoJson} />
                    </MapContainer>
                  </div>
                  {/* Floating action buttons */}
                  <div className="absolute bottom-8 left-8 flex space-x-3" style={{ zIndex: 1000 }}>
                    <button
                      type="submit"
                      form="edit-hub-form"
                      className="inline-flex items-center px-6 py-3 border border-transparent shadow-2xl text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
                    >
                      <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedHub(null);
                      }}
                      className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-2xl text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedHub && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Delete Hub</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete {selectedHub.name}? This action cannot be undone.
                    {selectedHub.planterCount && selectedHub.planterCount > 0 && (
                      <span className="block mt-2 text-red-600 font-medium">
                        Warning: This hub has {selectedHub.planterCount} church planter(s) assigned.
                        They must be reassigned before deleting this hub.
                      </span>
                    )}
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => deleteHubMutation.mutate(selectedHub.id)}
                    disabled={(selectedHub.planterCount || 0) > 0}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedHub(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Hubs;
